/** The other ways in: not everything arrives by dragging a file.
 *
 * Three routes, each said as a status and a link, never as a promise:
 *
 * - **Email.** There is no inbox address: forwarding bills to the workspace is not built (the v5
 *   scope lists it as staged, and `ingestion` has one adapter, a local file drop). The row says
 *   *not available yet* rather than showing an address that would take mail to nowhere. When the
 *   route exists it is data from the server, and this row reads it; the SPA holds no address.
 * - **Connected feeds.** Read live from the connector list. A failed read says it failed (a
 *   workspace with no feed and a read that did not answer are different claims), and a feed that has
 *   stopped is said first, because a census of what arrived says nothing about a feed that stopped.
 * - **A spreadsheet.** The bulk import, which maps columns once and saves the mapping.
 */

import { Link } from "@tanstack/react-router";
import { EnvelopeSimple, Plugs, Table } from "@phosphor-icons/react";

import { useConnectors } from "../../api/ingestion";
import { ReadFailed } from "../../components/Loaded";
import { StatusChip } from "../../components/ui";
import { keepSearch } from "../search";
import styles from "./OtherWays.module.css";

export function OtherWays({
  mayReadFeeds,
  mayManageFeeds = false,
}: {
  mayReadFeeds: boolean;
  /** Connectors are managed under Admin, Integrations (the company Admin's alone); anyone else
   * is shown the status without a link that would take them to a refusal. */
  mayManageFeeds?: boolean;
}) {
  const feeds = useConnectors(mayReadFeeds);
  const list = feeds.data ?? [];
  const stopped = list.filter((connector) => connector.stopped).length;

  let feedStatus: React.ReactNode;
  if (!mayReadFeeds || feeds.isPending) {
    feedStatus = <span className={styles.muted}>Reading</span>;
  } else if (feeds.error) {
    feedStatus = (
      <ReadFailed
        of="The status of the connected feeds"
        error={feeds.error}
        onRetry={() => void feeds.refetch()}
      />
    );
  } else if (list.length === 0) {
    feedStatus = <span className={styles.muted}>None connected</span>;
  } else if (stopped > 0) {
    feedStatus = (
      <StatusChip tone="crit" size="sm">
        {stopped} of {list.length} stopped
      </StatusChip>
    );
  } else {
    feedStatus = (
      <StatusChip tone="ok" size="sm">
        {list.length} connected
      </StatusChip>
    );
  }

  return (
    <ul className={styles.list} aria-label="Other ways to send data" data-testid="other-ways">
      <li className={styles.row} data-testid="way-email">
        <span className={styles.icon}>
          <EnvelopeSimple size={18} aria-hidden="true" />
        </span>
        <span className={styles.body}>
          <span className={styles.title}>Forward by email</span>
          <span className={styles.status}>
            <StatusChip tone="neutral" size="sm">Not available yet</StatusChip>
          </span>
        </span>
      </li>
      <li className={styles.row} data-testid="way-feeds">
        <span className={styles.icon}>
          <Plugs size={18} aria-hidden="true" />
        </span>
        <span className={styles.body}>
          {mayManageFeeds ? (
            <Link
              className={styles.title}
              to="/admin"
              search={(prev: Record<string, unknown>) => ({ ...keepSearch(prev), tab: "integrations" })}
            >
              Connected feeds
            </Link>
          ) : (
            <span className={styles.title}>Connected feeds</span>
          )}
          <span className={styles.status}>{feedStatus}</span>
        </span>
      </li>
      <li className={styles.row} data-testid="way-sheet">
        <span className={styles.icon}>
          <Table size={18} aria-hidden="true" />
        </span>
        <span className={styles.body}>
          <Link className={styles.title} to="/bulk" search={keepSearch}>
            Import a spreadsheet
          </Link>
          <span className={styles.status}>
            <span className={styles.muted}>Map the columns once</span>
          </span>
        </span>
      </li>
    </ul>
  );
}
